import React, { MouseEventHandler, ReactNode, useRef } from 'react';
import {
	CSSObject,
	ListItem,
	ListItemButton as MuiListItemButton,
	ListItemIcon as MuiListItemIcon,
	ListItemText as MuiListItemText,
	ListItemTextProps,
	styled,
	Theme,
} from '@mui/material';
import { Link, NavLink } from 'react-router-dom';
import { NavLinkListItemCorner } from 'shared/_components/app-layout/nav/styles';
import { NAV_BAR_LIST_ITEM_HEIGHT } from 'shared/_components/app-layout/constants';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

function getListItemSx(theme: Theme): CSSObject {
	return {
		display: 'block',
		paddingLeft: 1,
		'&:not(.active)': {
			color: theme.palette.common.white,
		},
	};
}

const ListItemButton = styled(MuiListItemButton, {
	shouldForwardProp: (prop) => !['expanded', 'level'].includes(prop as string),
})<{ expanded: boolean; level?: number }>(({ theme, expanded, level }) => ({
	minHeight: NAV_BAR_LIST_ITEM_HEIGHT,
	justifyContent: expanded ? 'initial' : 'center',
	paddingInlineStart: theme.spacing(1.5 + 4 * (level ?? 0)),
	paddingInlineEnd: theme.spacing(1.5),
}));

const ListItemIcon = styled(MuiListItemIcon, {
	shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded, theme }) => ({
	color: 'inherit',
	minWidth: 0,
	marginRight: expanded ? theme.spacing(1) : 'auto',
	justifyContent: 'center',
}));

function ListItemText({ expanded, ...props }: ListItemTextProps & { expanded: boolean }) {
	return (
		<MuiListItemText
			{...props}
			sx={{ opacity: expanded ? 1 : 0 }}
			primaryTypographyProps={{
				textOverflow: 'ellipsis',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
			}}
		/>
	);
}

type Props = {
	icon?: ReactNode;
	expanded: boolean;
	text: ReactNode;
	level?: number;
};

type NavListItemProps = Props & {
	onClick?: MouseEventHandler<HTMLDivElement>;
	isGroupExpanded?: boolean;
};

export function NavListItem(props: NavListItemProps) {
	const { icon, expanded, onClick, text, isGroupExpanded, level } = props;

	return (
		<ListItem disablePadding sx={(theme) => getListItemSx(theme)}>
			<ListItemButton expanded={expanded} onClick={onClick} level={level}>
				{icon && <ListItemIcon expanded={expanded}>{icon}</ListItemIcon>}
				<ListItemText primary={text} expanded={expanded || !icon} />
				{isGroupExpanded === true && <ExpandLess />}
				{isGroupExpanded === false && <ExpandMore />}
			</ListItemButton>
		</ListItem>
	);
}

type NavLinkListItemProps = Props & {
	to: string;
	isNavLink?: boolean;
};

export function NavLinkListItem(props: NavLinkListItemProps) {
	const { icon, expanded, text, to, isNavLink = true, level } = props;
	const ref = useRef(null);

	return (
		<ListItem
			ref={ref}
			component={isNavLink ? NavLink : Link}
			to={to}
			disablePadding
			sx={(theme) => ({
				...getListItemSx(theme),
				'&.active': {
					backgroundColor: theme.palette.background.paper,
					transition: 'width 200ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
				},
			})}
			dense
		>
			<NavLinkListItemCorner expanded={expanded} />
			<ListItemButton dense expanded={expanded} level={level}>
				{icon && <ListItemIcon expanded={expanded}>{icon}</ListItemIcon>}
				<ListItemText primary={text} expanded={expanded || !icon} />
			</ListItemButton>
		</ListItem>
	);
}
